// Delete confirmation for one position×pool standing rate, showing the blast
// radius first (the VendorArchiveConfirmModal pattern). The counts are
// informational: deleting mutates nothing but the row itself.
//
// Two deliberate differences from that precedent:
//  - it renders an explicit ERROR branch. The vendor modal returns `null` on a
//    failed impact fetch, which is indistinguishable from "nothing to report" —
//    exactly the reading a staff member must not get before a destructive act.
//  - it never calls the delete "permanent". CommissionAllocations is a SQL
//    Server temporal table (migration 20404304000000 enables system versioning
//    on it), so the deleted row survives in its [history] shadow.

import { AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { commissionsApi, type CommissionAllocationDto } from "@/api/commissions";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";

export interface AllocationDeleteConfirmModalProps {
  allocation: CommissionAllocationDto;
  /** Fires only on the user's confirmation; the caller owns the mutation. */
  onConfirm: () => void;
  onCancel: () => void;
  deleting?: boolean;
  /** A failed delete. Without this the modal just stays open on failure, which
   *  reads as "nothing happened" and invites a repeat request. */
  error?: string | null;
}

function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

export function AllocationDeleteConfirmModal({
  allocation,
  onConfirm,
  onCancel,
  deleting = false,
  error = null,
}: AllocationDeleteConfirmModalProps) {
  const impactQuery = useQuery({
    queryKey: commissionKeys.allocationDeleteImpact(allocation.id),
    queryFn: () => commissionsApi.allocationDeleteImpact(allocation.id),
  });
  const impact = impactQuery.data;

  return (
    <Modal
      open
      onClose={onCancel}
      dismissOnBackdropClick={false}
      title={`Delete ${allocation.roleCode} / ${allocation.poolCode}?`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={deleting}
            data-testid="allocation-delete-confirm"
          >
            Delete rate
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-onyx m-0">
          This removes the standing{" "}
          <span className="font-semibold tabular-nums">{allocation.percent.toFixed(2)}%</span> rate
          for <span className="font-semibold">{allocation.roleTitle}</span> on pool{" "}
          {allocation.poolCode}. Claims already drafted keep their own recorded rate and amount —
          they are not recalculated.
        </p>

        {/* The impact summary is swapped in after the dialog has already been
            announced, so without a live region a screen-reader user hears
            "Checking impact…" and is never told the blast radius. */}
        <div aria-live="polite" className="flex flex-col gap-space-3">
        {impactQuery.isLoading ? (
          <p className="text-small text-gray-mid m-0">Checking impact…</p>
        ) : impactQuery.isError ? (
          <div
            className="flex items-start gap-space-2 p-space-3 border border-status-warning rounded-md bg-status-warning/5 text-small text-onyx"
            data-testid="allocation-delete-impact-error"
          >
            <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
            <span>
              Could not load the impact summary. You can still delete, but you are doing so without
              it.
            </span>
          </div>
        ) : impact ? (
          <div className="flex flex-col gap-space-1">
            <p
              className="text-small font-semibold text-onyx m-0"
              data-testid="allocation-delete-impact-counts"
            >
              {plural(impact.claimCount, "existing claim")} ·{" "}
              {plural(impact.roleHolderCount, "current holder")} ·{" "}
              {plural(impact.orphanedOverrideCount, "per-user override")}
            </p>
            <p className="text-small text-gray-mid m-0">
              {impact.poolHasRecognizedIncome
                ? `Pool ${impact.poolCode} has recognized income, so this position is being suggested today — that stops.`
                : `Pool ${impact.poolCode} has no recognized income yet, so nothing is being suggested from this rate today.`}
            </p>
            {/* Two materially different outcomes. The rates grid derives which
                positions it shows from the allocation table itself, so removing
                a position's LAST row drops its holders out of the grid rather
                than blanking one cell. */}
            {impact.roleHolderCount > 0 && (
              <p className="text-small text-gray-mid m-0" data-testid="allocation-delete-grid-note">
                {impact.otherAllocationCountForPosition > 0 ? (
                  <>
                    The rates grid will show{" "}
                    <span className="tabular-nums font-semibold">0.00%</span> for holders of{" "}
                    {impact.roleCode} on this pool, which reads as a chosen rate rather than an
                    absent one.
                  </>
                ) : (
                  <>
                    This is {impact.roleCode}&apos;s only allocation, so its holders will drop out
                    of the rates grid entirely rather than showing a zero.
                  </>
                )}
              </p>
            )}
            {impact.orphanedOverrideCount > 0 && (
              <p className="text-small text-gray-mid m-0">
                {plural(impact.orphanedOverrideCount, "per-user override")} on this pool will
                survive and keep applying, with no position default beneath them.
              </p>
            )}
          </div>
        ) : null}
        </div>

        {error && (
          <div
            className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-small text-status-error"
            role="alert"
            data-testid="allocation-delete-error"
          >
            <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}
